import { useTranslation } from 'react-i18next';
import { FileText, DollarSign, Receipt, AlertTriangle, Loader2 } from 'lucide-react';
import { Avatar } from '@brainsway/ui';
import { FormModal, MODAL_COLORS } from '@/components/modal';
import { useUserRelatedRecords, useDeleteUser } from '../hooks';
import toast from 'react-hot-toast';
import type { UserListItem } from '../types';

interface DeleteUserConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserListItem;
  onDeleted: () => void;
}

export function DeleteUserConfirmModal({ isOpen, onClose, user, onDeleted }: DeleteUserConfirmModalProps) {
  const { t } = useTranslation();
  const { data: related, isLoading } = useUserRelatedRecords(isOpen ? user.id : null);
  const deleteMutation = useDeleteUser();

  const hasRecords = related && (related.reports > 0 || related.payrollEntries > 0 || related.payrollRuns > 0);
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  const handleSubmit = async () => {
    await deleteMutation.mutateAsync(user.id);
    toast.success(t('users.deleteSuccess'));
    onClose();
    onDeleted();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('users.delete')}
      onSubmit={isLoading ? undefined : handleSubmit}
      isPending={deleteMutation.isPending}
      submitLabel={hasRecords ? t('users.deleteConfirmModal.confirmWithRecords') : t('users.deleteConfirmModal.confirmClean')}
      maxWidth="max-w-md"
    >
      <div className="flex flex-col gap-3">
        {/* User info card */}
        <div
          className="flex items-center gap-3 p-3 rounded-xl"
          style={{
            backgroundColor: MODAL_COLORS.interactive,
            border: `1px solid ${MODAL_COLORS.border}`,
          }}
        >
          <Avatar name={displayName} src={user.avatarUrl} size="md" />
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-sm truncate" style={{ color: MODAL_COLORS.text }}>
              {displayName}
            </span>
            <span className="text-xs opacity-60 truncate">{user.email}</span>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: MODAL_COLORS.text }} />
          </div>
        )}

        {/* Related records warning */}
        {!isLoading && hasRecords && (
          <>
            <div
              className="flex items-start gap-2.5 p-3 rounded-xl"
              style={{
                backgroundColor: 'rgba(254, 226, 226, 0.6)',
                border: '1px solid rgba(185, 28, 28, 0.15)',
              }}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#B91C1C' }} />
              <span className="text-sm font-medium" style={{ color: '#B91C1C' }}>
                {t('users.deleteConfirmModal.warning')}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {related.reports > 0 && (
                <RecordRow icon={FileText} label={t('users.deleteConfirmModal.reports')} count={related.reports} />
              )}
              {related.payrollEntries > 0 && (
                <RecordRow icon={DollarSign} label={t('users.deleteConfirmModal.payrollEntries')} count={related.payrollEntries} />
              )}
              {related.payrollRuns > 0 && (
                <RecordRow icon={Receipt} label={t('users.deleteConfirmModal.payrollRuns')} count={related.payrollRuns} />
              )}
            </div>
          </>
        )}

        {/* No related records */}
        {!isLoading && !hasRecords && related && (
          <p className="text-sm text-center py-2 opacity-70">
            {t('users.deleteConfirmModal.noRelatedRecords')}
          </p>
        )}
      </div>
    </FormModal>
  );
}

function RecordRow({ icon: Icon, label, count }: { icon: typeof FileText; label: string; count: number }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 rounded-lg"
      style={{
        backgroundColor: MODAL_COLORS.interactive,
        border: `1px solid ${MODAL_COLORS.border}`,
      }}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: MODAL_COLORS.text, opacity: 0.6 }} />
        <span className="text-sm" style={{ color: MODAL_COLORS.text }}>{label}</span>
      </div>
      <span className="text-sm font-bold" style={{ color: '#B91C1C' }}>{count}</span>
    </div>
  );
}
